// frontend/packages/business-console/src/pages/StripeConnectSettings.jsx
//
// Stripe Connect onboarding + management surface for tenants ("Payment
// settings" in the nav; the Payments dashboard links here via "Payout
// settings"). Mounts four Connect Embedded Components against a single
// AccountSession scoped to all four (the cheapest call shape: one mint,
// four mountable surfaces). Per D7, tenants never visit
// connect.stripe.com; the entire onboarding flow lives in this page, and
// ALL bank/KYC collection happens inside Stripe's embedded components.
// The chrome around them (step rail, trust panel) is ours; the inputs
// are Stripe's.
//
// Bootstrap order (see the useEffect below):
//   1. POST /v2/stripe-connect/account - idempotently creates the
//      Express account on first visit, returns its current status mirror
//      (+ the tenant's applicationFeeBps for the pricing copy) AND the
//      platform publishable key the Stripe SDK needs.
//   2. loadConnectAndInitialize() with publishableKey + fetchClientSecret.
//   3. <ConnectComponentsProvider> wraps the components below.
//
// businessadmin-only on the backend; non-admins get a 403 surfaced as
// the error UI. Styling consumes the shared redesign kit
// (components/primitives) so it matches the Payments dashboard.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CheckIcon from '@mui/icons-material/Check';

import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
  ConnectPayouts,
} from '@stripe/react-connect-js';

import {
  getOrCreateStripeConnectAccount,
  createStripeConnectAccountSession,
} from '@upryzze/services';
import { BusinessContext } from '../contexts/BusinessContext';
import {
  useTokens,
  StatusPill,
  Surface,
  GradientHeroCard,
} from '../components/primitives';

// We request all four allowlisted components on every session mint.
// AccountSessions don't cost anything to scope broadly, and pre-
// authorizing all four means we can rearrange or hide individual
// components below without a corresponding backend change.
const ALL_COMPONENTS = [
  'account_onboarding',
  'payouts',
  'account_management',
  'notification_banner',
];

function accountStatusPill(status) {
  if (!status) return null;
  const { chargesEnabled, payoutsEnabled, detailsSubmitted } = status;
  if (chargesEnabled && payoutsEnabled) {
    return <StatusPill kind="success" dot label="Payouts enabled · Verified" />;
  }
  if (detailsSubmitted) {
    return (
      <StatusPill
        kind="warning"
        dot
        label="Submitted · awaiting Stripe verification"
      />
    );
  }
  return <StatusPill kind="neutral" dot label="Onboarding not complete" />;
}

// ── Pricing copy from the tenant's real fee config ────────────────────
//
// Locked decision: show the effective rate, not Stripe-only pricing. The
// platform fee shows up in every payment's net, so the copy must match
// what the tenant will actually see. `applicationFeeBps` comes from the
// /account response; null means the fee isn't configured yet.

function pricingCopy(applicationFeeBps) {
  if (typeof applicationFeeBps === 'number' && applicationFeeBps > 0) {
    const pct = (applicationFeeBps / 100).toLocaleString('en-US', {
      maximumFractionDigits: 2,
    });
    return `2.9% + 30¢ per card payment to Stripe, plus a ${pct}% UpRyzze platform fee. ACH bank payments cost less. No monthly fee.`;
  }
  if (applicationFeeBps === 0) {
    return '2.9% + 30¢ per card payment to Stripe. ACH bank payments cost less. No monthly fee, and no platform fee on your current plan.';
  }
  return "Stripe's processing fee per transaction plus the UpRyzze platform fee for your plan. No monthly fee.";
}

// ── Setup step rail ───────────────────────────────────────────────────
//
// Derived from the status mirror, NOT collected here: the actual inputs
// live inside Stripe's embedded onboarding component below. This rail
// just tells the tenant where they are.

function stepsFromStatus(status) {
  const detailsDone = Boolean(status?.detailsSubmitted);
  const payoutsDone = Boolean(status?.payoutsEnabled);
  const verified = Boolean(
    status?.chargesEnabled &&
    status?.payoutsEnabled &&
    (status?.requirementsDue || []).length === 0
  );
  return [
    {
      title: 'Business details',
      subtitle: 'Legal name, EIN, and contact info.',
      done: detailsDone,
    },
    {
      title: 'Bank account for payouts',
      subtitle: 'Where your money lands. We never see the full number.',
      done: payoutsDone,
    },
    {
      title: 'Verify your identity',
      subtitle: 'A photo ID, required once by law. Takes a minute.',
      done: verified,
    },
  ];
}

function StepRail({ status }) {
  const t = useTokens();
  const steps = stepsFromStatus(status);
  const doneCount = steps.filter((s) => s.done).length;
  const activeIndex = steps.findIndex((s) => !s.done);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '18px' }}>
        {steps.map((s, i) => (
          <Box
            key={s.title}
            sx={{
              flex: 1,
              height: 5,
              borderRadius: '3px',
              bgcolor: s.done
                ? t.green
                : i === activeIndex
                  ? t.blue
                  : t.grayBorder,
            }}
          />
        ))}
        <Typography
          sx={{ fontSize: 11, fontWeight: 800, color: t.muted, ml: '4px' }}
        >
          {doneCount} of {steps.length}
        </Typography>
      </Box>
      {steps.map((s, i) => (
        <Box
          key={s.title}
          sx={{
            display: 'flex',
            gap: '14px',
            py: '13px',
            borderBottom:
              i === steps.length - 1 ? 'none' : `1px solid ${t.hairline}`,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              fontWeight: 900,
              ...(s.done
                ? { bgcolor: t.green, color: '#fff' }
                : i === activeIndex
                  ? { bgcolor: t.blue, color: '#fff' }
                  : {
                      bgcolor: 'transparent',
                      border: `2px solid ${t.grayBorder}`,
                      color: t.faint,
                    }),
            }}
          >
            {s.done ? <CheckIcon sx={{ fontSize: 15 }} /> : i + 1}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 800,
                color: s.done || i === activeIndex ? t.text : t.textSecondary,
              }}
            >
              {s.title}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: s.done ? t.muted : t.faint,
                mt: '2px',
              }}
            >
              {s.subtitle}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              alignSelf: 'center',
              color: s.done ? t.green : i === activeIndex ? t.blue : t.faint,
            }}
          >
            {s.done ? 'Done' : i === activeIndex ? 'Up next' : 'Next'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Trust panel (right rail during onboarding) ────────────────────────

function TrustPanel({ applicationFeeBps }) {
  const t = useTokens();
  const bullets = [
    {
      title: 'Your money, your bank.',
      body: 'Funds are held by Stripe and paid to you. UpRyzze never touches them.',
    },
    {
      title: 'Clear pricing.',
      body: pricingCopy(applicationFeeBps),
    },
    {
      title: 'Bank-grade security.',
      body: 'Card data is encrypted by Stripe. You are never liable for it.',
    },
  ];
  return (
    <Box
      sx={{
        width: { xs: '100%', md: 320 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <GradientHeroCard sx={{ p: '18px' }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, opacity: 0.85 }}>
          Once you&apos;re live
        </Typography>
        <Typography
          sx={{ fontSize: 13, lineHeight: 1.5, mt: 1, fontWeight: 600 }}
        >
          An invoice gets paid by card and the money, net of fees, hits your
          bank the next business day: no checks, no chasing.
        </Typography>
      </GradientHeroCard>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          mt: '22px',
        }}
      >
        {bullets.map((b) => (
          <Box key={b.title} sx={{ display: 'flex', gap: '11px' }}>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '6px',
                bgcolor: t.greenBg,
                color: t.green,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <CheckIcon sx={{ fontSize: 13 }} />
            </Box>
            <Typography
              sx={{ fontSize: 12.5, color: t.textSecondary, lineHeight: 1.45 }}
            >
              <Box component="b" sx={{ color: t.text }}>
                {b.title}
              </Box>{' '}
              {b.body}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography
        sx={{
          mt: 'auto',
          pt: '18px',
          fontSize: 11,
          color: t.faint,
          fontWeight: 600,
        }}
      >
        Securely powered by{' '}
        <Box component="b" sx={{ color: t.stripe, fontSize: 13 }}>
          Stripe
        </Box>
      </Typography>
    </Box>
  );
}

function SectionCard({ title, children }) {
  const t = useTokens();
  return (
    <Surface sx={{ p: 2 }}>
      <Typography
        sx={{ fontSize: 13, fontWeight: 800, color: t.text, mb: 1.5 }}
      >
        {title}
      </Typography>
      {children}
    </Surface>
  );
}

export default function StripeConnectSettings() {
  const t = useTokens();
  const { business } = useContext(BusinessContext);
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  const [bootstrap, setBootstrap] = useState({
    loading: true,
    error: null,
    status: null,
  });
  const [connectInstance, setConnectInstance] = useState(null);

  // Stripe SDK callback. Called automatically by the SDK on initial mount
  // of any Connect component and again whenever the previous
  // AccountSession is about to expire. We don't memoize the result: each
  // call hits our backend and gets a fresh client_secret.
  const fetchClientSecret = useCallback(async () => {
    const { data } = await createStripeConnectAccountSession(ALL_COMPONENTS);
    return data.clientSecret;
  }, []);

  // Refetch the status mirror without rebuilding the Connect instance.
  // Called from ConnectAccountOnboarding's onExit so the pill and the
  // step rail update as soon as the tenant finishes the flow.
  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await getOrCreateStripeConnectAccount();
      setBootstrap((prev) => ({ ...prev, status: data }));
    } catch {
      // Non-fatal: the user can refresh the page to retry.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getOrCreateStripeConnectAccount();
        if (cancelled) return;

        if (!data.publishableKey) {
          setBootstrap({
            loading: false,
            error: new Error(
              'Stripe Connect is not configured for this environment. Contact UpRyzze support.'
            ),
            status: data,
          });
          return;
        }

        const instance = loadConnectAndInitialize({
          publishableKey: data.publishableKey,
          fetchClientSecret,
          appearance: {
            overlays: 'dialog',
            variables: { colorPrimary: '#2563eb' },
          },
        });

        if (cancelled) return;
        setConnectInstance(instance);
        setBootstrap({ loading: false, error: null, status: data });
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const message =
          status === 403
            ? 'Only a business admin can manage payment settings.'
            : err?.response?.data?.message ||
              err?.message ||
              'Failed to load Stripe Connect.';
        setBootstrap({
          loading: false,
          error: new Error(message),
          status: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchClientSecret]);

  const status = bootstrap.status;
  const verified = Boolean(status?.chargesEnabled && status?.payoutsEnabled);

  const headerContent = useMemo(
    () => (
      <Box>
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 900,
            letterSpacing: '-0.6px',
            color: t.text,
          }}
        >
          Payment settings
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: '6px',
            flexWrap: 'wrap',
          }}
        >
          {accountStatusPill(status)}
          <Typography sx={{ fontSize: 11, color: t.faint, fontWeight: 600 }}>
            Powered by{' '}
            <Box component="b" sx={{ color: t.stripe }}>
              Stripe
            </Box>
          </Typography>
          {verified ? (
            <Typography
              component={RouterLink}
              to={`${basePath}/payments`}
              sx={{
                fontSize: 11.5,
                fontWeight: 800,
                color: t.blue,
                textDecoration: 'none',
                ml: 0.5,
              }}
            >
              Open the Payments dashboard
            </Typography>
          ) : null}
        </Box>
      </Box>
    ),
    [status, t, verified, basePath]
  );

  if (bootstrap.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (bootstrap.error) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        {headerContent}
        <Alert severity="error" sx={{ mt: 3 }}>
          {bootstrap.error.message}
        </Alert>
      </Box>
    );
  }

  if (!connectInstance) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        {headerContent}
        <Alert severity="warning" sx={{ mt: 3 }}>
          Stripe Connect could not be initialized.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1060, mx: 'auto' }}>
      {headerContent}

      <ConnectComponentsProvider connectInstance={connectInstance}>
        <Box sx={{ mt: 3 }}>
          <ConnectNotificationBanner />
        </Box>

        {!verified ? (
          // ── Onboarding: step rail + embedded flow, trust panel right ──
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              mt: 2,
            }}
          >
            <Box sx={{ flex: 1.4, minWidth: 0 }}>
              <StatusPill
                kind="ai"
                label={
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <CreditCardOutlinedIcon sx={{ fontSize: 13 }} />
                    Set up payments
                  </Box>
                }
              />
              <Typography
                sx={{
                  fontSize: 25,
                  fontWeight: 900,
                  letterSpacing: '-0.7px',
                  m: '16px 0 6px',
                  color: t.text,
                }}
              >
                Get paid right in the console
              </Typography>
              <Typography
                sx={{
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  color: t.muted,
                  mb: '22px',
                  maxWidth: 460,
                }}
              >
                Three quick steps and your invoices can take cards, Apple Pay
                and bank transfers. Money goes straight to your account.
              </Typography>

              <StepRail status={status} />

              <Surface sx={{ mt: 3, p: 2 }}>
                <ConnectAccountOnboarding onExit={refreshStatus} />
              </Surface>
            </Box>

            <TrustPanel applicationFeeBps={status?.applicationFeeBps ?? null} />
          </Box>
        ) : (
          // ── Steady state: account management + payouts ──
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <SectionCard title="Payouts">
              <ConnectPayouts />
            </SectionCard>
            <SectionCard title="Account">
              <ConnectAccountManagement />
            </SectionCard>
            <Typography sx={{ fontSize: 11, color: t.faint, fontWeight: 600 }}>
              {pricingCopy(status?.applicationFeeBps ?? null)} Securely powered
              by{' '}
              <Box component="b" sx={{ color: t.stripe, fontSize: 12 }}>
                Stripe
              </Box>
            </Typography>
          </Box>
        )}
      </ConnectComponentsProvider>
    </Box>
  );
}
